import React, { useState, useRef, useEffect } from "react";
import { Row, Col, Form, Button } from "react-bootstrap";
import { GuestForm } from "./GuestForm";
import { fetchGuilds } from "../../../utils/api/helpers/guild";
import { createRsvp, checkEmail } from "../../../utils/api/helpers/rsvp";
import CaptchaCanvas from "./Captcha/CaptchaCanvas";
import { useCaptcha } from "./Captcha/useCaptcha";
import { useBotGuards } from "./Captcha/useBotGuards";

const EventRSVPForm = ({
  event_slug,
  allowGuests,
  title,
  start_date,
  start_time,
  guestThreshold,
}) => {
  const [guild_list, setGuildList] = useState([]),
    [formSubmitClicked, setFormSubmitClicked] = useState(false),
    [formSubmitted, setFormSubmitted] = useState(false),
    [duplicateRSVP, setDuplicateRSVP] = useState(false);

  const [name, setName] = useState(""),
    [email, setEmail] = useState(""),
    [guild_affiliation, setGuildAffiliation] = useState(null),
    [venue_id, setVenueId] = useState(null),
    [guestList, setGuestList] = useState([]),
    [rsvp_status, setRsvpStatus] = useState(null);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCheckingDuplicate, setIsCheckingDuplicate] = useState(false);
  const [guildLoadError, setGuildLoadError] = useState("");
  const [submitError, setSubmitError] = useState("");
  const [captchaError, setCaptchaError] = useState("");
  const [guestError, setGuestError] = useState("");
  const [generalValidationError, setGeneralValidationError] = useState("");

  const mountedRef = useRef(true);
  const duplicateCheckIdRef = useRef(0);

  const captcha = useCaptcha({ length: 6 });
  const guards = useBotGuards({
    minFillTimeMs: 5 * 1000,
    maxFailures: 5,
    lockoutMs: 30 * 1000,
  });

  const emailIsValid = (value) => {
    const trimmed = String(value || "").trim();
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed);
  };

  const person = () => {
    return {
      name: name.trim(),
      email: email.trim(),
      guild_id: guild_affiliation,
      venue_id: venue_id,
      guests: guestList,
    };
  };

  const refreshCaptchaOnly = () => {
    captcha.refresh();
  };

  const handleManualCaptchaRefresh = () => {
    captcha.refresh();
    setCaptchaError("");
  };

  const hardResetProtection = () => {
    captcha.refresh();
    guards.resetFormTimer();
    setCaptchaError("");
  };

  const resetFormState = () => {
    setFormSubmitClicked(false);
    setFormSubmitted(false);
    setDuplicateRSVP(false);
    setName("");
    setEmail("");
    setGuildAffiliation("");
    setVenueId("");
    setGuestList([]);
    setRsvpStatus(null);
    setIsSubmitting(false);
    setIsCheckingDuplicate(false);
    setSubmitError("");
    setCaptchaError("");
    setGuestError("");
    setGeneralValidationError("");
    guards.clearFailures();
    hardResetProtection();
  };

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (event_slug) {
      resetFormState();
    }
  }, [event_slug]);

  useEffect(() => {
    let subscribed = true;

    const loadGuilds = async () => {
      try {
        setGuildLoadError("");
        const result = await fetchGuilds();

        if (!subscribed) return;

        if (result && !result.error && Array.isArray(result.data)) {
          setGuildList(result.data);
        } else {
          setGuildList([]);
          setGuildLoadError("Unable to load guild affiliations.");
        }
      } catch (error) {
        if (!subscribed) return;
        setGuildList([]);
        setGuildLoadError("Unable to load guild affiliations.");
      }
    };

    loadGuilds();

    return () => {
      subscribed = false;
    };
  }, []);

  const updateGuestList = (idx, guest, remove) => {
    setGuestError("");

    let l = guestList.slice();

    if (remove) {
      l = l.filter((el) => {
        return el.name !== guest.name;
      });
      setGuestList(l);
      return;
    }

    if (idx < 0) {
      if (guestThreshold > 0 && l.length >= guestThreshold) {
        return;
      }
      l.push(guest);
    } else {
      l[idx] = guest;
    }

    setGuestList(l);
  };

  const doCheckBlankGuests = (list) => {
    if (!list.length) return false;

    const hasBlanks = list.some((o) => {
      return !o || String(o.name || "").trim() === "";
    });

    return hasBlanks;
  };

  const doCheckEmail = async (nextName = name, nextEmail = email) => {
    const trimmedName = String(nextName || "").trim();
    const trimmedEmail = String(nextEmail || "").trim();

    if (
      !trimmedName ||
      !trimmedEmail ||
      !emailIsValid(trimmedEmail) ||
      !event_slug
    ) {
      if (mountedRef.current) {
        setDuplicateRSVP(false);
      }
      return false;
    }

    const currentCheckId = duplicateCheckIdRef.current + 1;
    duplicateCheckIdRef.current = currentCheckId;

    try {
      if (mountedRef.current) {
        setIsCheckingDuplicate(true);
      }

      const res = await checkEmail(event_slug, trimmedName, trimmedEmail);

      if (
        !mountedRef.current ||
        duplicateCheckIdRef.current !== currentCheckId
      ) {
        return false;
      }

      const isDuplicate = !!(res && res.data && res.data.valid === false);
      setDuplicateRSVP(isDuplicate);
      return isDuplicate;
    } catch (error) {
      if (
        mountedRef.current &&
        duplicateCheckIdRef.current === currentCheckId
      ) {
        setDuplicateRSVP(false);
      }
      return false;
    } finally {
      if (
        mountedRef.current &&
        duplicateCheckIdRef.current === currentCheckId
      ) {
        setIsCheckingDuplicate(false);
      }
    }
  };

  const submitRsvp = async () => {
    const result = await createRsvp(event_slug, person());
    return result;
  };

  const validateForm = () => {
    const currentPerson = person();
    const hasBlankGuests = doCheckBlankGuests(guestList);
    const tooManyGuests =
      allowGuests && guestThreshold > 0 && guestList.length > guestThreshold;

    setGuestError("");
    setGeneralValidationError("");
    setCaptchaError("");

    if (hasBlankGuests) {
      setGuestError("Please complete or remove any blank guest entries.");
    } else if (tooManyGuests) {
      setGuestError(
        `You may only RSVP with up to ${guestThreshold} guest${
          guestThreshold > 1 ? "s" : ""
        }.`,
      );
    }

    if (!currentPerson.name) return false;
    if (!emailIsValid(currentPerson.email)) return false;
    if (!guild_affiliation || guild_affiliation === "Select Guild Affiliation")
      return false;
    if (duplicateRSVP) return false;
    if (hasBlankGuests || tooManyGuests) return false;
    if (!captcha.isValid) {
      setCaptchaError("Captcha Code does not match, please try again");
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    if (e && typeof e.preventDefault === "function") {
      e.preventDefault();
    }

    setFormSubmitClicked(true);
    setSubmitError("");
    setGeneralValidationError("");
    setCaptchaError("");

    guards.registerSubmit();

    const guardCheck = guards.validateBeforeSubmit();
    if (!guardCheck.ok) {
      setGeneralValidationError(guardCheck.message);
      hardResetProtection();
      return;
    }

    const isDuplicate = await doCheckEmail(name, email);
    if (isDuplicate) {
      hardResetProtection();
      return;
    }

    const isFormValid = validateForm();
    if (!isFormValid) {
      if (!captcha.isValid) {
        setCaptchaError("Captcha Code does not match, please try again");
        refreshCaptchaOnly();
      }
      return;
    }

    try {
      setIsSubmitting(true);

      const result = await submitRsvp();

      if (!mountedRef.current) return;

      guards.clearFailures();
      setRsvpStatus(result);
      setFormSubmitted(true);
    } catch (error) {
      if (!mountedRef.current) return;

      setSubmitError(
        "There was a problem submitting your RSVP. Please try again.",
      );
      hardResetProtection();
    } finally {
      if (mountedRef.current) {
        setIsSubmitting(false);
      }
    }
  };

  const ReloadIcon = () => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      width="24"
      height="24"
      fill="none"
    >
      <path
        d="M20 12a8 8 0 1 1-2.34-5.66"
        stroke="#000"
        strokeWidth="2"
        strokeLinecap="round"
      />
      <path
        d="M20 4v5h-5"
        stroke="#000"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );

  return (
    <div className="rsvpform__container w-100 d-flex flex-column justify-content-start">
      {!formSubmitted ? (
        <Form onSubmit={handleSubmit}>
          <div
            style={{
              position: "absolute",
              left: "-9999px",
              top: "auto",
              width: "1px",
              height: "1px",
              overflow: "hidden",
            }}
          >
            <label htmlFor="website">Leave this field empty</label>
            <input id="website" type="text" {...guards.honeypotProps} />
          </div>

          <div className={"row"}>
            <Col lg={12}>
              <Form.Group controlId="signup_name">
                <Form.Control
                  value={name}
                  required
                  onChange={(e) => {
                    setName(e.target.value);
                    setDuplicateRSVP(false);
                    setSubmitError("");
                  }}
                  onBlur={() => doCheckEmail(name, email)}
                  placeholder="Name"
                  className={"nf-sans-cond mb-2"}
                />
              </Form.Group>

              {formSubmitClicked && name.trim() === "" ? (
                <div className="validation__message">Please enter a Name</div>
              ) : (
                ""
              )}

              {formSubmitClicked && duplicateRSVP ? (
                <div className="validation__message">
                  This person has already RSVP'd to this event
                </div>
              ) : (
                ""
              )}
            </Col>
          </div>

          <div className={"row"}>
            <div className={"col-12 col-md-6 mb-2 pe-md-0"}>
              <Form.Group controlId="signup_email">
                <Form.Control
                  type="email"
                  required
                  value={email}
                  placeholder="Email"
                  className={"nf-sans-cond"}
                  onBlur={() => doCheckEmail(name, email)}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    setDuplicateRSVP(false);
                    setSubmitError("");
                  }}
                />
              </Form.Group>

              {formSubmitClicked && !emailIsValid(email) ? (
                <div className="validation__message mt-2">
                  Please enter a valid email address
                </div>
              ) : (
                ""
              )}
            </div>

            <div className={"col-12 col-md-6 ps-md-2 mb-2"}>
              <Form.Group controlId="signup_event">
                <Form.Control
                  as="select"
                  required
                  value={guild_affiliation || ""}
                  onChange={(e) => {
                    setGuildAffiliation(e.target.value);
                    setSubmitError("");
                  }}
                >
                  <option value="">Select Guild Affiliation</option>

                  {guild_list.map((ga, i) => {
                    return (
                      <option key={i} value={ga.guild_id}>
                        {ga.name}
                      </option>
                    );
                  })}
                </Form.Control>
              </Form.Group>

              {formSubmitClicked &&
              (!guild_affiliation ||
                guild_affiliation === "Select Guild Affiliation") ? (
                <div className="validation__message mt-2">
                  Please select a Guild Affiliation
                </div>
              ) : (
                ""
              )}

              {guildLoadError ? (
                <div className="validation__message mt-2">{guildLoadError}</div>
              ) : (
                ""
              )}
            </div>

            {allowGuests && guestThreshold > 0 ? (
              <div className="py-2 w-100 mb-4">
                <div className="row">
                  <div className="col-12 d-flex align-items-center">
                    <div
                      className="mb-1 py-2 fw-bold"
                      style={{ fontSize: "1.5rem" }}
                    >
                      GUEST(S)
                    </div>

                    {guestList.length < guestThreshold ? (
                      <Button
                        type="button"
                        className="ms-auto rsvp-button"
                        onClick={() =>
                          updateGuestList(-1, {
                            name: "",
                          })
                        }
                      >
                        <span className="label-small">+ Add Guest</span>
                      </Button>
                    ) : (
                      <span className="text-uppercase ps-2 ps-md-3 lh-1 ">
                        Limit {guestThreshold} {guestThreshold > 1 ? "s" : ""}{" "}
                        per RSVP
                      </span>
                    )}
                  </div>
                </div>

                {guestList
                  ? guestList.map((gl, i) => (
                      <GuestForm
                        key={i}
                        idx={i}
                        updateGuest={updateGuestList}
                        formSubmitClicked={formSubmitClicked}
                      />
                    ))
                  : ""}

                {formSubmitClicked && guestError ? (
                  <div className="validation__message">{guestError}</div>
                ) : (
                  ""
                )}
              </div>
            ) : (
              ""
            )}

            <div className="mb-0 d-flex flex-column flex-md-row ">
              <div className="d-flex flex-column flex-sm-row align-items-stretch rsvp-captcha-container pe-0 pe-md-2 gap-2">
                <div>
                  <CaptchaCanvas
                    text={captcha.challenge}
                    refreshSeed={captcha.refreshSeed}
                    width={220}
                    height={72}
                  />
                </div>

                <Button
                  type="button"
                  // className="rsvp-button"
                  style={{
                    backgroundColor: "transparent",
                    outline: "none",
                    borderColor: "transparent",
                    padding: "0",
                  }}
                  onClick={handleManualCaptchaRefresh}
                >
                  <span className="label-small">
                    <ReloadIcon />
                  </span>
                </Button>

                <div
                  style={{
                    display: "flex",
                    alignItems: "stretch",
                    width: "100%",
                  }}
                >
                  <Form.Control
                    {...captcha.inputProps}
                    placeholder="Enter Captcha Code"
                    onChange={(e) => {
                      captcha.inputProps.onChange(e);
                      if (captchaError) {
                        setCaptchaError("");
                      }
                    }}
                  />
                </div>
              </div>

              <div className="flex-grow-1 mt-3 mt-md-0 d-flex align-items-stretch justify-content-center">
                <Button
                  type="submit"
                  className="rsvp-button submit-button w-100"
                  disabled={
                    isSubmitting || guards.isLocked || isCheckingDuplicate
                  }
                >
                  {isSubmitting ? "Submitting..." : "Submit Your RSVP"}
                </Button>
              </div>
            </div>
          </div>

          {guards.isLocked ? (
            <Row
              className="justify-content-center font-weight-bold pt-4 text-uppercase"
              style={{ fontSize: "1.5rem" }}
            >
              Too many failed attempts. Try again in{" "}
              {Math.ceil(guards.remainingLockMs / 1000)} seconds.
            </Row>
          ) : null}

          {formSubmitClicked && captchaError ? (
            <Row
              className="justify-content-center font-weight-bold pt-4 text-uppercase"
              style={{ fontSize: "1.5rem" }}
            >
              {captchaError}
            </Row>
          ) : null}

          {generalValidationError ? (
            <Row
              className="justify-content-center font-weight-bold pt-4 text-uppercase"
              style={{ fontSize: "1.5rem" }}
            >
              {generalValidationError}
            </Row>
          ) : null}

          {submitError ? (
            <Row
              className="justify-content-center font-weight-bold pt-4 text-uppercase"
              style={{ fontSize: "1.5rem" }}
            >
              {submitError}
            </Row>
          ) : null}
        </Form>
      ) : (
        <div className={"text-uppercase"} style={{ fontSize: "1.5rem" }}>
          {isSubmitting ? (
            <div className="text-center my-5">Sending RSVP...</div>
          ) : (
            <>
              {(() => {
                switch (rsvp_status) {
                  case "DUPE_EMAIL":
                    return (
                      <div className="dupeuser text-center my-5 fw-bold">
                        This name has already been submitted for this event.
                      </div>
                    );
                  case "CONFIRM":
                    return (
                      <div className="confirm text-center my-5 fw-bold">
                        Thank you for submitting your RSVP to {title}!<br />
                        <br />
                        You will receive a reply via email shortly.
                      </div>
                    );
                  case "DENY":
                    return (
                      <div className="deny text-center my-5 fw-bold">
                        Capacity for {title} has been reached.
                      </div>
                    );
                  case "GUEST_EXCEEDED":
                    return (
                      <div className="guestexceed text-center my-5">
                        Your guests exceed the number of allowed guests for{" "}
                        <span className="font-weight-bold">{title}</span>
                        . <br />
                        <br />
                        Please refresh this page and re-submit with fewer
                        guests.
                      </div>
                    );
                  case "REQUEST":
                    return (
                      <div className="request text-center my-5">
                        Thank you for submitting your RSVP to{" "}
                        <span className="font-weight-bold">{title}</span>
                        ! <br />
                        <br />
                        You will receive a reply via email shortly.
                      </div>
                    );
                  default:
                    return (
                      <div className="default text-center my-5">
                        Thank you for submitting your RSVP to{" "}
                        <span className="font-weight-bold">{title}</span>
                        !<br />
                        <br />
                        You will receive a reply via email shortly.
                      </div>
                    );
                }
              })()}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default EventRSVPForm;
